import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "../../../components/AppToast";
import CommonDataTable from "../../../components/CommonDataTable";
import CreatePricingFoundation from "./CreatePricingFoundation";
import { fetchPricingFoundationList } from "../../../services/pricingFoundation/pricingFoundation";
import "./pricingFoundation.css";

const FORM_TITLES = {
  create: "Create Pricing Foundation",
  edit: "Edit Pricing Foundation",
  view: "View Pricing Foundation",
};

export default function PricingFoundationList() {
  const [tableRows, setTableRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [formMode, setFormMode] = useState(null);
  const [formEditData, setFormEditData] = useState(null);
  const [blockedIds, setBlockedIds] = useState(() => new Set());
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [search, setSearch] = useState("");

  // Load pricing foundation list from API
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchPricingFoundationList(page, pageSize);
      
      // Handle different response structures
      const data = Array.isArray(result) ? result : result?.items || result?.records || [];
      const total = result?.total || result?.count || data.length;

      const formatted = data.map((item) => ({
        id: item.id || item._id,
        skuid: item.skuId || item.skuid,
        description: item.description,
        originalPrice: item.originalPrice || item.original_price,
        sellingPrice: item.sellingPrice || item.selling_price,
        customer:
          item.customerName ||
          item.customer_name ||
          item.supplierName ||
          item.supplier_name ||
          item.customer ||
          item.sellerOrganization ||
          item.seller_organization,
        customerGroup: item.customerGroup || item.customer_group || "",
        status: item.status || "Active",
        isBlocked: item.isBlocked || item.is_blocked || false,
        __original: item,
      }));

      setTableRows(formatted);
      setTotalCount(total);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load pricing foundation records");
      setTableRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    if (!formMode) loadList();
  }, [loadList, formMode, page, pageSize]);

  const displayRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tableRows.filter((row) => {
      if (q) {
        const sku = String(row.skuid || "").toLowerCase();
        const desc = String(row.description || "").toLowerCase();
        const sup = String(row.customer || "").toLowerCase();
        const grp = String(row.customerGroup || "").toLowerCase();
        if (!sku.includes(q) && !desc.includes(q) && !sup.includes(q) && !grp.includes(q)) return false;
      }
      return true;
    });
  }, [tableRows, search]);

  const clearFilters = () => {
    setSearch("");
  };

  const filtersActive = Boolean(search.trim());

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const closeForm = () => {
    setFormMode(null);
    setFormEditData(null);
  };

  const buildEditDataFromRow = (row) => {
    const original = row.__original || {};
    return {
      id: row.id,
      _id: row.id,
      skuid: row.skuid,
      skuId: row.skuid,
      description: row.description,
      originalPrice: row.originalPrice,
      sellingPrice: row.sellingPrice,
      customer: row.customer,
      customerGroup: row.customerGroup,
      ...original,
    };
  };

  const openPricingForm = (row, mode) => {
    handleCloseMenu();
    if (mode === "create") {
      setFormEditData(null);
      setFormMode("create");
      return;
    }
    if (!row) return;
    setFormEditData(buildEditDataFromRow(row));
    setFormMode(mode);
  };

  const handleView = (row) => openPricingForm(row, "view");
  const handleEdit = (row) => openPricingForm(row, "edit");

  const actionColumn = useMemo(
    () => ({
      field: "__actions",
      headerName: "",
      width: 72,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleOpenMenu(e, params.row)} aria-label="row actions">
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    }),
    []
  );

  const rawColumns = useMemo(
    () => [
      {
        field: "skuid",
        headerName: "SKU ID",
        width: 120,
      },
      { field: "description", headerName: "Description", flex: 1, minWidth: 200 },
      {
        field: "originalPrice",
        headerName: "Purchase Price",
        width: 130,
        renderCell: (p) => `$${Number(p.value || 0).toFixed(2)}`,
      },
      {
        field: "sellingPrice",
        headerName: "Selling Price",
        width: 130,
        renderCell: (p) => `$${Number(p.value || 0).toFixed(2)}`,
      },
      { field: "customer", headerName: "Supplier", width: 180 },
      { field: "customerGroup", headerName: "Supplier group", width: 150 },
      {
        field: "statusUi",
        headerName: "Status",
        width: 110,
        sortable: false,
        renderCell: (p) =>
          blockedIds.has(p.row.id) ? (
            <span className="badge rounded-pill text-bg-danger">Blocked</span>
          ) : (
            <span className="badge rounded-pill text-bg-success">Active</span>
          ),
      },
    ],
    [blockedIds]
  );

  const allPossibleColumns = useMemo(() => [actionColumn, ...rawColumns], [actionColumn, rawColumns]);
  const initialVisible = useMemo(() => allPossibleColumns.map((c) => c.field), [allPossibleColumns]);

  if (formMode) {
    const skuLabel = formEditData?.skuid || formEditData?.skuId || "";
    const breadcrumbLabel =
      formMode === "create"
        ? "New Pricing Record"
        : formMode === "edit"
          ? `Edit Pricing — ${skuLabel}`
          : `View Pricing — ${skuLabel}`;

    return (
      <Card className="pricing-foundation-list-card border-0 shadow-sm">
        <div className="card-header bg-secondary text-white py-2">
          <h4 className="mb-0 h6 fw-semibold">
            {FORM_TITLES[formMode]}
            {skuLabel && formMode !== "create" ? ` — ${skuLabel}` : ""}
          </h4>
        </div>
        <nav className="px-3 py-2 border-bottom bg-light" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <button type="button" className="btn btn-link p-0 text-decoration-none text-muted" onClick={closeForm}>
                Pricing Foundation
              </button>
            </li>
            <li className="breadcrumb-item active fw-semibold text-primary" aria-current="page">
              {breadcrumbLabel}
            </li>
          </ol>
        </nav>
        <Card.Body>
          <CreatePricingFoundation
            onClose={closeForm}
            onSuccess={() => {
              closeForm();
              setPage(0);
              loadList();
            }}
            editData={formMode !== "create" ? formEditData : null}
            readOnly={formMode === "view"}
          />
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="pricing-foundation-list-card border-0 shadow-sm mb-3">
        <Card.Header className="bg-white py-3 border-bottom">
          <Row className="g-2 align-items-center">
            <Col xs={12} md="auto">
              <h5 className="mb-0 text-primary fw-semibold">Pricing Foundation</h5>
              {/* <div className="small text-secondary">SKU pricing and seller information</div> */}
            </Col>
            <Col xs={12} lg={4} className="ms-lg-auto">
              <InputGroup size="sm">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-search" />
                </span>
                <Form.Control
                  className="border-start-0"
                  placeholder="Search SKU ID or description…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search Pricing"
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5 text-secondary small">Loading…</div>
          ) : (
            <CommonDataTable
              allPossibleColumns={allPossibleColumns}
              initialVisible={initialVisible}
              initialRows={displayRows}
              height={filtersActive ? Math.max(360, displayRows.length * 52 + 120) : 520}
              rowsPerPageOptions={[10, 25, 50, 100]}
              exportImportPath="PricingFoundation"
              forItems
              forInventory
              showCreateButton={true}
              createButtonText="Create Pricing"
              createButtonIcon="bi bi-plus-lg"
              onCreateClick={() => openPricingForm(null, "create")}
              page={filtersActive ? 0 : page}
              pageSize={filtersActive ? Math.max(displayRows.length, 1) : pageSize}
              rowCount={filtersActive ? displayRows.length : totalCount}
              onPageChange={(p) => {
                if (!filtersActive) setPage(p);
              }}
              onPageSizeChange={(ps) => {
                if (!filtersActive) {
                  setPageSize(ps);
                  setPage(0);
                }
              }}
              onDataRefresh={() => {}}
            />
          )}
          {filtersActive && (
            <div className="px-3 py-2 border-top bg-light small text-secondary">
              Showing {displayRows.length} row(s) after filters on this page.
            </div>
          )}
        </Card.Body>
        <Card.Footer className="bg-light py-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="small text-secondary">
            Server page {page + 1} · {totalCount} total (filters apply to loaded rows)
          </div>
          <div className="btn-group btn-group-sm">
            <Button variant="outline-secondary" disabled={page <= 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Prev
            </Button>
            <Button variant="outline-secondary" disabled={loading || (page + 1) * pageSize >= totalCount} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </Card.Footer>
      </Card>

      <Menu open={Boolean(menuAnchorEl)} anchorEl={menuAnchorEl} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleView(menuRow)}>
          <i className="bi bi-eye me-2" /> View
        </MenuItem>
        <MenuItem onClick={() => handleEdit(menuRow)}>
          <i className="bi bi-pencil me-2" /> Edit
        </MenuItem>
      </Menu>
    </>
  );
}